// @flow
import React from "react";
import { connect } from "react-redux";
import { translate } from "../../IntlProviderWrapper";
import Order from "./lineOrderList/Order.tsx";
import { sortUnscheduled } from "./reducers/linesOrders.tsx";
import { getOrgData } from "../../../lib/Auth";
import { classNames } from "../../../lib/Util";
import { LinesOrdersLogic } from "./reducers/BusinessLogic.tsx";

import type { ReduxState } from "./reducers/reducers.tsx";
import type {
    LineOrders,
    IOrderProducedModelEx,
    OperationSortBy,
    OperationSortDirection,
} from "./reducers/linesOrders.tsx";
import type { PlanningTableFiltersState } from "./reducers/reducersFilters/filters.tsx";
import type { PlanningTableColumn } from "../../ViewsCommon.tsx";

type Props = {
    show_unscheduled_orders: boolean,
    unscheduled_orders: LineOrders[],
    filters: PlanningTableFiltersState,
    selected_materials: string[],
    unscheduled_sort_by: OperationSortBy,
    unscheduled_sort_direction: OperationSortDirection,
    columns: PlanningTableColumn[],
    campaign_reference_key: string | null,
    last_clicked_order_uuid: string | null,
};

type State = {};

class UnscheduledOrders extends React.Component<Props, State> {
    getAllUnscheduledOrders = (): IOrderProducedModelEx[] => {
        const orders = [];
        for (const line of this.props.unscheduled_orders) {
            orders.push(...line.orders);
        }
        return sortUnscheduled(orders, this.props.unscheduled_sort_by, this.props.unscheduled_sort_direction);
    };

    renderOrder = (order: IOrderProducedModelEx) => {
        const is_selected = order.uuid === this.props.last_clicked_order_uuid;

        return (
            <tbody
                key={order.uuid}
                className={classNames("order-group-body", { "order-group-body--selected": is_selected })}
            >
                <Order
                    order_uuid={order.uuid}
                    capacity_factor={order.capacity_factor}
                    sim_ignore_earliest_start={order.sim_ignore_earliest_start}
                    sortIndex={0}
                    enabled={!order.skip_sim}
                    line_uuid={order.line_uuid}
                    enabled_edit={false}
                    is_unscheduled={true}
                    columns={this.props.columns}
                    campaign_group={order.campaign_group}
                />
            </tbody>
        );
    };

    renderUnscheduledOrders = () => {
        // We don't show alternate colors for unscheduled orders,
        // only the reference color when a target order is selected
        const reference_only = true;

        const unscheduled_orders = LinesOrdersLogic.addCampaignGroups(
            this.getAllUnscheduledOrders(),
            this.props.campaign_reference_key,
            reference_only
        );

        if (unscheduled_orders.length > 0) {
            return unscheduled_orders.map(this.renderOrder);
        }

        return (
            <tbody>
                <tr>
                    <td colSpan={this.props.columns.length}>
                        <div className="planning-table__no-orders">{translate("common.no_data", "No data")}</div>
                    </td>
                </tr>
            </tbody>
        );
    };

    render() {
        const { columns } = this.props;

        return (
            <div className="planning-table__row mt-0" style={{ caretColor: "transparent", overflow: "auto" }}>
                <div className="planning-table__row-title"></div>
                <table className="planning-table__sortable-list">
                    <thead>
                        <tr>
                            {columns.map(column => {
                                const style = {
                                    minWidth: column.min_width || "auto",
                                    // Trick to make the ellipsis work with variable widths
                                    maxWidth: column.min_width || "auto",
                                    textAlign: column.align || "auto",
                                };

                                return (
                                    <th
                                        key={column.id}
                                        title={column.value}
                                        className={`sortable-element__${column.id}`}
                                        style={style}
                                    >
                                        {column.value}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    {this.renderUnscheduledOrders()}
                </table>
            </div>
        );
    }
}

export default connect((state: ReduxState, own_props: Props) => {
    const planning_table = state.gantt_chart_planning_table;
    const show_unscheduled_orders = planning_table.show_unscheduled_orders;
    const lines_orders = state.gantt_chart_lines_orders;
    const selected_materials = state.gantt_chart_filters.selected_materials;
    const filters = state.gantt_chart_filters;

    let unscheduled_orders = lines_orders.unscheduled_orders;
    if (getOrgData().code == "agrana") {
        // Remove "translucent" orders
        const valid_orders_map = state.gantt_chart_planning_table.show_valid_lines.valid_orders_map;
        if (valid_orders_map !== null) {
            unscheduled_orders = unscheduled_orders.map(line_orders => {
                return {
                    ...line_orders,
                    orders: line_orders.orders.filter(order => valid_orders_map.has(order.uuid)),
                };
            });
        }
    }

    return {
        show_unscheduled_orders,
        unscheduled_orders,
        selected_materials,
        filters,
        unscheduled_sort_by: lines_orders.unscheduled_sort_by,
        unscheduled_sort_direction: lines_orders.unscheduled_sort_direction,
        highlighted_move_order_uuid: planning_table.highlighted_move_order_uuid,
        campaign_reference_key: planning_table.campaign_reference_key,
        last_clicked_order_uuid: planning_table.last_clicked_order_uuid,
    };
}, null)(UnscheduledOrders);
